import React, { forwardRef, useState, useEffect } from 'react'
import {
  Card,
  Text,
  Stack,
  Box,
  Heading,
  Button,
  Spinner,
  Flex,
  Badge,
  Checkbox,
  Select,
  Label,
} from '@sanity/ui'
import { ObjectInputProps, useFormValue, useClient } from 'sanity'

interface DescriptionResult {
  generated: boolean
  skipped?: boolean
  reason?: string
  content?: string
  error?: string
}

interface ListingResult {
  fitmentTypeIndex: number
  listingIndex: number
  wheelSetup: string
  wheelSpecs: {
    front: string
    rear: string
  }
  descriptions: Record<string, DescriptionResult>
}

interface GenerationResponse {
  success: boolean
  summary: {
    totalListings: number
    processed: number
    generated: number
    skipped: number
    failed: number
  }
  results: ListingResult[]
  metadata: {
    totalCost: number
    totalTokens: number
    processingTimeMs: number
  }
  rules?: {
    id: string
    name: string
    version: string
  }
}

// Preview types
interface PreviewDescriptionResult {
  content: string
  tokens: { input: number; output: number }
  cost: number
  validation?: {
    isValid: boolean
    issues: string[]
    length: number
  }
  error?: string
}

interface PreviewListingResult {
  fitmentTypeIndex: number
  listingIndex: number
  fitmentTypeName: string
  wheelSpecs: {
    front: string
    rear: string
  }
  descriptions: Record<string, PreviewDescriptionResult>
}

interface PreviewResponse {
  success: boolean
  results: PreviewListingResult[]
  summary: {
    totalCost: number
    totalTokens: number
    estimatedFullCost: number
    totalListingsInGuide: number
    previewedCount: number
    processingTimeMs: number
  }
  rules?: {
    id: string
    name: string
    version: string
  }
}

interface AIRuleset {
  _id: string
  name: string
  version: string
  isActive: boolean
}

const DESCRIPTION_TYPE_LABELS: Record<string, string> = {
  overview: 'Overview',
  performanceAnalysis: 'Performance Analysis',
  easeOfFitment: 'Ease of Fitment',
  tireAvailability: 'Tire Availability',
}

const AIGenerationButton = forwardRef<HTMLDivElement, ObjectInputProps>(
  (props, ref) => {
    // Get document ID using Sanity v4 hook
    const rawId = useFormValue(['_id']) as string | undefined
    const documentId = rawId?.replace('drafts.', '')
    const client = useClient({ apiVersion: '2024-01-01' })

    // Ruleset state
    const [rulesets, setRulesets] = useState<AIRuleset[]>([])
    const [selectedRulesId, setSelectedRulesId] = useState<string>('')
    const [loadingRulesets, setLoadingRulesets] = useState(true)

    // Generation state
    const [loading, setLoading] = useState(false)
    const [results, setResults] = useState<GenerationResponse | null>(null)
    const [error, setError] = useState('')
    const [forceRegenerate, setForceRegenerate] = useState(false)

    // Preview state
    const [previewMode, setPreviewMode] = useState<'idle' | 'configuring' | 'loading' | 'results'>('idle')
    const [sampleSize, setSampleSize] = useState(3)
    const [previewResults, setPreviewResults] = useState<PreviewResponse | null>(null)
    const [expandedListings, setExpandedListings] = useState<Set<number>>(new Set())

    // Fetch available rulesets on mount
    useEffect(() => {
      const fetchRulesets = async () => {
        try {
          const result = await client.fetch<AIRuleset[]>(
            `*[_type == "aiGenerationRules"]{ _id, name, version, isActive } | order(isActive desc, name asc)`
          )
          setRulesets(result || [])
          // Set default to active ruleset or first available
          const activeRuleset = result?.find((r) => r.isActive)
          if (activeRuleset) {
            setSelectedRulesId(activeRuleset._id)
          } else if (result?.length > 0) {
            setSelectedRulesId(result[0]._id)
          }
        } catch (err) {
          console.error('Error fetching rulesets:', err)
        } finally {
          setLoadingRulesets(false)
        }
      }
      fetchRulesets()
    }, [client])

    const handleGenerateDescriptions = async () => {
      if (!documentId) {
        setError('Document must be saved first')
        return
      }

      setLoading(true)
      setError('')
      setResults(null)
      setPreviewMode('idle')
      setPreviewResults(null)

      console.log('[AIGeneration] Starting generation for:', documentId)

      const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
      try {
        const res = await fetch(`${baseURL}/api/ai/generate-descriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buyerGuideId: documentId,
            force: forceRegenerate,
            rulesId: selectedRulesId || undefined,
          }),
        })

        console.log('[AIGeneration] Response status:', res.status)

        if (!res.ok) {
          const data = await res.json()
          console.error('[AIGeneration] Error response:', data)
          throw new Error(data.message || 'Failed to generate descriptions')
        }

        const data: GenerationResponse = await res.json()
        console.log('[AIGeneration] Success:', data.summary)
        setResults(data)
      } catch (err: any) {
        console.error('[AIGeneration] Caught error:', err)
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          setError(`Cannot connect to server. Make sure the frontend server is running at ${baseURL}`)
        } else {
          setError(err.message || 'Error generating descriptions')
        }
      } finally {
        setLoading(false)
      }
    }

    const handleRunPreview = async () => {
      if (!documentId) {
        setError('Document must be saved first')
        return
      }

      setPreviewMode('loading')
      setError('')
      setPreviewResults(null)
      setExpandedListings(new Set())

      console.log('[AIGeneration] Running preview for:', documentId, 'sampleSize:', sampleSize)

      const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'
      try {
        const res = await fetch(`${baseURL}/api/ai/preview-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            buyerGuideId: documentId,
            sampleSize,
            rulesId: selectedRulesId || undefined,
          }),
        })

        console.log('[AIGeneration] Preview response status:', res.status)

        if (!res.ok) {
          const data = await res.json()
          console.error('[AIGeneration] Preview error response:', data)
          throw new Error(data.message || 'Failed to run preview')
        }

        const data: PreviewResponse = await res.json()
        console.log('[AIGeneration] Preview success:', data.summary)
        setPreviewResults(data)
        setPreviewMode('results')
        // Expand first listing by default
        if (data.results.length > 0) {
          setExpandedListings(new Set([0]))
        }
      } catch (err: any) {
        console.error('[AIGeneration] Preview caught error:', err)
        if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
          setError(`Cannot connect to server. Make sure the frontend server is running at ${baseURL}`)
        } else {
          setError(err.message || 'Error running preview')
        }
        setPreviewMode('idle')
      }
    }

    const toggleListingExpanded = (index: number) => {
      setExpandedListings((prev) => {
        const next = new Set(prev)
        if (next.has(index)) {
          next.delete(index)
        } else {
          next.add(index)
        }
        return next
      })
    }

    const formatDuration = (ms: number): string => {
      if (ms < 1000) return `${ms}ms`
      if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
      return `${(ms / 60000).toFixed(1)}m`
    }

    return (
      <Stack gap={4} ref={ref}>
        {/* Preview Configuration Card */}
        {(previewMode === 'idle' || previewMode === 'configuring') && !results && (
          <Card padding={4} radius={2} shadow={1} tone="primary">
            <Stack gap={3}>
              <Heading size={1}>Preview AI Generation</Heading>
              <Text size={1} muted>
                Test your AI rules on a sample of listings before running on all fitments.
                No content is saved during preview.
              </Text>

              {/* Ruleset Selection */}
              <Box>
                <Label size={1} style={{ marginBottom: '8px', display: 'block' }}>AI Rules</Label>
                {loadingRulesets ? (
                  <Flex align="center" gap={2}>
                    <Spinner />
                    <Text size={1} muted>Loading rulesets...</Text>
                  </Flex>
                ) : rulesets.length === 0 ? (
                  <Text size={1} muted>No rulesets configured</Text>
                ) : (
                  <Select
                    value={selectedRulesId}
                    onChange={(e) => setSelectedRulesId(e.currentTarget.value)}
                  >
                    {rulesets.map((rs) => (
                      <option key={rs._id} value={rs._id}>
                        {rs.name} (v{rs.version}){rs.isActive ? ' • Active' : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Box>

              <Flex align="center" gap={3}>
                <Text size={1}>Sample Size:</Text>
                <Select
                  value={sampleSize.toString()}
                  onChange={(e) => setSampleSize(parseInt(e.currentTarget.value, 10))}
                  style={{ width: '80px' }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                <Text size={1} muted>listings</Text>
              </Flex>

              <Flex gap={2}>
                <Button
                  text="Run Preview"
                  tone="positive"
                  onClick={handleRunPreview}
                  disabled={!documentId}
                />
              </Flex>

              {!documentId && (
                <Text size={1} muted>
                  Save the document first to run preview.
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {/* Preview Loading */}
        {previewMode === 'loading' && (
          <Card padding={4} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>
                Generating preview for {sampleSize} listing{sampleSize > 1 ? 's' : ''}...
              </Text>
            </Flex>
          </Card>
        )}

        {/* Preview Results */}
        {previewMode === 'results' && previewResults && (
          <Card padding={4} radius={2} shadow={1} tone="primary">
            <Stack gap={4}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Heading size={1}>Preview Results ({previewResults.summary.previewedCount} listings)</Heading>
              </Flex>

              {/* Summary Stats */}
              <Card padding={3} radius={2} tone="default">
                <Stack gap={2}>
                  {previewResults.rules && (
                    <Text size={1}>
                      <strong>Using Rules:</strong> {previewResults.rules.name} (v{previewResults.rules.version})
                    </Text>
                  )}
                  <Flex gap={4} wrap="wrap">
                    <Text size={1}>
                      <strong>Preview Cost:</strong> ${previewResults.summary.totalCost.toFixed(4)}
                    </Text>
                    <Text size={1}>
                      <strong>Tokens:</strong> {previewResults.summary.totalTokens.toLocaleString()}
                    </Text>
                    <Text size={1}>
                      <strong>Est. Full Cost:</strong> ${previewResults.summary.estimatedFullCost.toFixed(2)}
                    </Text>
                    <Text size={1}>
                      <strong>Total Listings:</strong> {previewResults.summary.totalListingsInGuide}
                    </Text>
                  </Flex>
                </Stack>
              </Card>

              {/* Expandable Listing Results */}
              <Stack gap={2}>
                {previewResults.results.map((result, idx) => (
                  <Card
                    key={idx}
                    padding={3}
                    radius={2}
                    tone="default"
                    style={{ cursor: 'pointer' }}
                  >
                    <Stack gap={3}>
                      {/* Listing Header - Clickable */}
                      <Flex
                        align="center"
                        justify="space-between"
                        onClick={() => toggleListingExpanded(idx)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Flex align="center" gap={2}>
                          <Text size={1} weight="semibold">
                            {expandedListings.has(idx) ? '▼' : '▶'}
                          </Text>
                          <Text size={1} weight="semibold">
                            Listing {idx + 1}: {result.wheelSpecs.front}
                            {result.wheelSpecs.front !== result.wheelSpecs.rear && (
                              <span style={{ opacity: 0.6 }}> / {result.wheelSpecs.rear}</span>
                            )}
                          </Text>
                        </Flex>
                        <Text size={0} muted>
                          {result.fitmentTypeName}
                        </Text>
                      </Flex>

                      {/* Expanded Content */}
                      {expandedListings.has(idx) && (
                        <Stack gap={3} paddingLeft={4}>
                          {Object.entries(result.descriptions).map(([descType, desc]) => (
                            <Box key={descType}>
                              <Flex align="center" gap={2} marginBottom={2}>
                                <Text size={1} weight="semibold">
                                  {DESCRIPTION_TYPE_LABELS[descType] || descType}
                                </Text>
                                {desc.error ? (
                                  <Badge tone="critical" fontSize={0}>Error</Badge>
                                ) : (
                                  <Badge tone="positive" fontSize={0}>
                                    {desc.tokens.output} tokens
                                  </Badge>
                                )}
                              </Flex>
                              {desc.error ? (
                                <Card padding={2} radius={1} tone="critical">
                                  <Text size={1}>{desc.error}</Text>
                                </Card>
                              ) : (
                                <Card
                                  padding={3}
                                  radius={1}
                                  tone="default"
                                >
                                  <Text
                                    size={1}
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'system-ui, sans-serif',
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {desc.content}
                                  </Text>
                                </Card>
                              )}
                              {desc.validation && !desc.validation.isValid && (
                                <Card padding={2} radius={1} tone="caution" marginTop={2}>
                                  <Text size={0}>
                                    Validation issues: {desc.validation.issues.join(', ')}
                                  </Text>
                                </Card>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>

              {/* Action Buttons */}
              <Flex gap={3} marginTop={3}>
                <Button
                  text="Proceed with Full Generation"
                  tone="positive"
                  onClick={() => {
                    setPreviewMode('idle')
                    setPreviewResults(null)
                    handleGenerateDescriptions()
                  }}
                />
                <Button
                  text="Run Another Preview"
                  tone="default"
                  mode="ghost"
                  onClick={() => {
                    setPreviewMode('idle')
                    setPreviewResults(null)
                  }}
                />
              </Flex>
            </Stack>
          </Card>
        )}

        {/* Main Generation Card - Only show when not in preview results */}
        {previewMode !== 'results' && !previewResults && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={3}>
              <Heading size={1}>AI Content Generation</Heading>
              <Text size={1} muted>
                Generate AI-powered descriptions for all fitment listings in this buyer guide.
                This includes overview, performance analysis, ease of fitment, and tire availability content.
              </Text>
              <Text size={1} muted>
                Content is saved as a draft for review before publishing.
              </Text>

              {/* Ruleset Selection */}
              <Box>
                <Label size={1} style={{ marginBottom: '8px', display: 'block' }}>AI Rules</Label>
                {loadingRulesets ? (
                  <Flex align="center" gap={2}>
                    <Spinner />
                    <Text size={1} muted>Loading rulesets...</Text>
                  </Flex>
                ) : rulesets.length === 0 ? (
                  <Text size={1} muted>No rulesets configured</Text>
                ) : (
                  <Select
                    value={selectedRulesId}
                    onChange={(e) => setSelectedRulesId(e.currentTarget.value)}
                  >
                    {rulesets.map((rs) => (
                      <option key={rs._id} value={rs._id}>
                        {rs.name} (v{rs.version}){rs.isActive ? ' • Active' : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </Box>

              <Flex align="center" gap={2}>
                <Checkbox
                  checked={forceRegenerate}
                  onChange={(e) => setForceRegenerate(e.currentTarget.checked)}
                />
                <Text size={1}>Force regenerate (overwrite existing content)</Text>
              </Flex>

              <Button
                text={loading ? 'Generating...' : 'Generate AI Descriptions'}
                tone="primary"
                onClick={handleGenerateDescriptions}
                disabled={loading || !documentId || previewMode === 'loading' || loadingRulesets}
              />

              {!documentId && (
                <Text size={1} muted>
                  Save the document first to generate descriptions.
                </Text>
              )}
            </Stack>
          </Card>
        )}

        {error && (
          <Card padding={3} radius={2} tone="critical">
            <Text size={1}>{error}</Text>
          </Card>
        )}

        {loading && (
          <Card padding={4} radius={2}>
            <Flex align="center" justify="center" gap={3}>
              <Spinner />
              <Text size={1}>
                Generating AI descriptions... This may take a few minutes.
              </Text>
            </Flex>
          </Card>
        )}

        {results && (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Flex align="center" justify="space-between" wrap="wrap" gap={2}>
                <Heading size={1}>Generation Results</Heading>
                <Flex gap={2}>
                  <Badge tone="positive">{results.summary.generated} generated</Badge>
                  <Badge tone="caution">{results.summary.skipped} skipped</Badge>
                  {results.summary.failed > 0 && (
                    <Badge tone="critical">{results.summary.failed} failed</Badge>
                  )}
                </Flex>
              </Flex>

              {/* Summary Stats */}
              <Card padding={3} radius={2} tone="default">
                <Stack gap={2}>
                  {results.rules && (
                    <Text size={1}>
                      <strong>Used Rules:</strong> {results.rules.name} (v{results.rules.version})
                    </Text>
                  )}
                  <Flex gap={4} wrap="wrap">
                    <Text size={1}>
                      <strong>Total Listings:</strong> {results.summary.totalListings}
                    </Text>
                    <Text size={1}>
                      <strong>Total Tokens:</strong> {results.metadata.totalTokens.toLocaleString()}
                    </Text>
                    <Text size={1}>
                      <strong>Cost:</strong> ${results.metadata.totalCost.toFixed(4)}
                    </Text>
                    <Text size={1}>
                      <strong>Duration:</strong> {formatDuration(results.metadata.processingTimeMs)}
                    </Text>
                  </Flex>
                </Stack>
              </Card>

              {/* Results Table */}
              <Box
                style={{
                  maxHeight: '400px',
                  overflow: 'auto',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px',
                  }}
                >
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--card-bg-color)' }}>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'left',
                        }}
                      >
                        Wheel Setup
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '80px',
                        }}
                      >
                        Overview
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '80px',
                        }}
                      >
                        Perform.
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '80px',
                        }}
                      >
                        Ease
                      </th>
                      <th
                        style={{
                          padding: '10px 8px',
                          borderBottom: '2px solid var(--card-border-color)',
                          textAlign: 'center',
                          width: '80px',
                        }}
                      >
                        Tires
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((r, idx) => (
                      <tr key={idx}>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid var(--card-border-color)',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                          }}
                        >
                          {r.wheelSpecs.front}
                          {r.wheelSpecs.front !== r.wheelSpecs.rear && (
                            <span style={{ opacity: 0.6 }}> / {r.wheelSpecs.rear}</span>
                          )}
                        </td>
                        {['overview', 'performanceAnalysis', 'easeOfFitment', 'tireAvailability'].map(
                          (descType) => {
                            const desc = r.descriptions[descType]
                            let tone: 'positive' | 'caution' | 'critical' = 'positive'
                            let label = 'OK'

                            if (!desc) {
                              tone = 'caution'
                              label = '—'
                            } else if (desc.error) {
                              tone = 'critical'
                              label = 'ERR'
                            } else if (desc.skipped) {
                              tone = 'caution'
                              label = 'SKIP'
                            }

                            return (
                              <td
                                key={descType}
                                style={{
                                  padding: '8px',
                                  borderBottom: '1px solid var(--card-border-color)',
                                  textAlign: 'center',
                                }}
                              >
                                <Badge tone={tone} style={{ fontSize: '10px' }}>
                                  {label}
                                </Badge>
                              </td>
                            )
                          }
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>

              {/* Status Legend */}
              <Flex gap={3} wrap="wrap">
                <Text size={0} muted>
                  Status:
                </Text>
                <Flex gap={2} align="center">
                  <Badge tone="positive">OK</Badge>
                  <Text size={0} muted>Generated</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="caution">SKIP</Badge>
                  <Text size={0} muted>Unchanged</Text>
                </Flex>
                <Flex gap={2} align="center">
                  <Badge tone="critical">ERR</Badge>
                  <Text size={0} muted>Failed</Text>
                </Flex>
              </Flex>

              <Card padding={3} radius={2} tone="positive">
                <Text size={1}>
                  Changes have been saved to a draft. Review and publish when ready.
                </Text>
              </Card>
            </Stack>
          </Card>
        )}
      </Stack>
    )
  }
)

AIGenerationButton.displayName = 'AIGenerationButton'

export default AIGenerationButton
